import { Ionicons } from '@expo/vector-icons';
import { router } from 'expo-router';
import React, { useEffect, useRef, useState } from 'react';
import { Pressable, View } from 'react-native';

import {
	ComposerAttachments,
	NO_ATTACHABLE,
	totalAttachmentBytes,
	type ComposerAttachmentItem,
} from '@/components/composer/composer-attachments';
import { previousLetterNote } from '@/components/inbox/auto-reply-notice';
import { ActivityIndicator, Button, ComposerWindow, Text, TextInput, useTheme } from '@/components/ui';
import { useComposerDraft } from '@/hooks/use-composer-draft';
import {
	useBoardApprovalPreview, useSendBoardApprovalEmail, useStageBoardApprovalAttachment, useUpdateApplication,
} from '@/hooks/use-data';
import { dataService, type BoardApprovalSendResult, type BoardApprovalVersion } from '@/services/data-service';
import { REVIEW_ACCENT } from '@/styles/theme';
import { applicationStatusLabel, ExteriorApplicationStatus } from '@/utils/application-status';
import { formatDocumentSize } from '@/utils/document-download';

/**
 * The "Waiting for Board Approval" letter, before it goes out (#555).
 *
 * `NeedsInfoModal`'s sibling, and deliberately the same window: the gate banner, blockers vs
 * muted notes, the opt-in status move. What it adds is the ATTACHMENT.
 *
 * ── NOTHING GOES OUT UNTIL THE PM PRESSES SEND ──
 * Opening this renders the application PDF on the server and STAGES it — nothing is sent and
 * the request does not move. The PDF appears here as a real attachment the PM can open, and
 * Send carries their own subject, their own body and the token of that exact file. Closing
 * the window changes nothing.
 */

export interface BoardApprovalModalProps {
	applicationId: string;
	visible: boolean;
	onDismiss: () => void;
	/** Fired after the server answers so the parent can report the outcome. */
	onSent: (result: BoardApprovalSendResult) => void;
	/** A rejected send — a 409, a removed template, a timeout. Nothing else reports it. */
	onError?: (error: unknown) => void;
	/** "Don't Send" moved the request to Waiting for Board Approval with no letter. Fired before close. */
	onMovedWithoutSending?: () => void;
	/** "Don't Send" was refused — the status move failed, and nothing changed. */
	onMoveError?: (error: unknown) => void;
	startMinimized?: boolean;
	expandRequest?: number;
	onDirtyChange?: (dirty: boolean) => void;
	draftScope?: string | null;
}

/** Only the reviewer's own words and their explicit choice — see `NeedsInfoDraft`. */
interface BoardApprovalDraft {
	subject: string;
	body: string;
	updateStatus: boolean | null;
	/** The request version the draft's words were written against. Absent on older drafts. */
	textVersion?: BoardApprovalVersion | null;
	/**
	 * Documents the PM UNticked (#608). Persisted, or a reload would silently tick them again
	 * and Send would attach a file someone deliberately left out.
	 */
	excludedDocumentIds?: string[];
}

const sameVersion = (a: BoardApprovalVersion | null | undefined, b: BoardApprovalVersion | null | undefined): boolean =>
	!!a && !!b && typeof a.contentVersion === 'string' && a.contentVersion === b.contentVersion
		&& a.status === b.status && a.updatedAt === b.updatedAt;

export const BoardApprovalModal: React.FC<BoardApprovalModalProps> = ({
	applicationId, visible, onDismiss, onSent, onError, onMovedWithoutSending, onMoveError,
	startMinimized, expandRequest, onDirtyChange, draftScope,
}) => {
	const theme = useTheme();
	const {
		data: preview, isLoading, error, isFetching: previewFetching, refetch: refetchPreview,
	} = useBoardApprovalPreview(applicationId, { enabled: visible });
	const send = useSendBoardApprovalEmail();
	const moveOnly = useUpdateApplication();

	const [subject, setSubject] = useState('');
	const [body, setBody] = useState('');
	const [dirty, setDirty] = useState(false);
	// `null` = untouched, so it keeps tracking the server default across refetches. The re-seed
	// effect must never write it — see the long note on the same state in `NeedsInfoModal`.
	const [updateStatus, setUpdateStatus] = useState<boolean | null>(null);
	/**
	 * The request version the WORDS on screen were written against — not the preview's latest.
	 *
	 * A refetch re-seeds untouched text, and the version moves with it. But edited text is kept
	 * across a refetch (retyping a letter is miserable), and it was written about the request as
	 * it WAS: "with the Board" on a request approved since. Sending the preview's new version
	 * with those words would pass every server check. So edited words keep their own version,
	 * and Send waits until the PM reconciles them with the change.
	 */
	const [textVersion, setTextVersion] = useState<BoardApprovalVersion | null>(null);
	/**
	 * The request documents the PM UNticked (#608). Stored as exclusions, so every document —
	 * including one that appears on a refetch — rides along unless someone takes it off.
	 */
	const [unticked, setUnticked] = useState<ReadonlySet<string>>(new Set());
	/**
	 * The selection changed. Its OWN flag, not `dirty`: `dirty` also freezes the letter text
	 * against refetches, and unticking a file must not make the PM's words look edited.
	 */
	const [selectionDirty, setSelectionDirty] = useState(false);
	/**
	 * Files ADDED beside the packet and the request's documents (#606): uploads and Dropbox picks.
	 * Not drafted — a staged file outlives a reload only briefly, and re-picking is cheap.
	 */
	const [added, setAdded] = useState<ComposerAttachmentItem[]>([]);
	const [addedBusy, setAddedBusy] = useState(false);

	useEffect(() => {
		if (!preview || dirty) return;
		setSubject(preview.subject);
		setBody(preview.bodyText);
		setTextVersion(preview.version);
	}, [preview, dirty]);

	useEffect(() => {
		if (!visible) {
			setDirty(false); setUpdateStatus(null); setTextVersion(null); setUnticked(new Set()); setSelectionDirty(false);
			setAdded([]);
			restoredRef.current = null;
		}
	}, [visible]);

	const draft = useComposerDraft<BoardApprovalDraft>(draftScope, visible ? `board-approval:${applicationId}` : null);
	const restoredRef = useRef<string | null>(null);
	useEffect(() => {
		if (!visible || !draft.ready || !draft.restored) return;
		if (restoredRef.current === applicationId) return;
		restoredRef.current = applicationId;
		setSubject(draft.restored.subject);
		setBody(draft.restored.body);
		setUpdateStatus(draft.restored.updateStatus);
		// An older draft saved no version: unknown counts as out of date, so it is reconfirmed.
		setTextVersion(draft.restored.textVersion ?? null);
		setDirty(true);
		if (draft.restored.excludedDocumentIds?.length) {
			setUnticked(new Set(draft.restored.excludedDocumentIds));
			setSelectionDirty(true);
		}
	}, [visible, applicationId, draft.ready, draft.restored]);

	useEffect(() => {
		if (!visible || (!dirty && !selectionDirty)) return;
		draft.save({ subject, body, updateStatus, textVersion, excludedDocumentIds: [...unticked] });
	}, [visible, dirty, selectionDirty, subject, body, updateStatus, textVersion, unticked, draft]);

	// An unticked file is work in progress too: the host asks before discarding it.
	// A file still being staged is unsaved work too: closing mid-upload would drop it without a word.
	useEffect(() => {
		onDirtyChange?.(visible && (dirty || selectionDirty || addedBusy));
	}, [visible, dirty, selectionDirty, addedBusy, onDirtyChange]);

	/**
	 * Render + stage the PDF as soon as the letter can be sent at all. Idle-gated, so it runs
	 * once per open; `reset()` (close, or a send that found the file gone) makes it run again.
	 */
	const stage = useStageBoardApprovalAttachment();
	const { mutate: stageMutate, reset: stageReset, isIdle: stageIdle } = stage;
	const staged = stage.data ?? null;
	// Not while the preview is refetching: staging against a request the text has not caught
	// up with would just produce another mismatch.
	useEffect(() => {
		if (!visible) { stageReset(); return; }
		if (preview?.canSend && stageIdle && !previewFetching) stageMutate(applicationId);
	}, [visible, preview?.canSend, stageIdle, previewFetching, stageMutate, stageReset, applicationId]);

	/**
	 * The text and the PDF must describe the same moment of the request — the server refuses a
	 * send where they do not. The preview and the staging are separate requests, so an edit or a
	 * decision can land between them: refresh the text AND re-stage, and Send stays disabled
	 * until the two agree again.
	 */
	const versionsAgree = sameVersion(preview?.version, staged?.version);
	useEffect(() => {
		if (!preview || !staged || versionsAgree || previewFetching) return;
		void refetchPreview();
		stageReset();
	}, [preview, staged, versionsAgree, previewFetching, refetchPreview, stageReset]);

	const gate = preview?.emailGate;
	const willSend = gate?.willActuallySend ?? false;
	const busy = send.isPending || moveOnly.isPending;
	const moveStatus = updateStatus ?? preview?.statusChange.willChange ?? false;

	// Mirrors the Send button's `disabled` — Cmd+Enter reaches `submit` directly. No Send until
	// the PDF the PM is looking at exists, and matches the text.
	const textCurrent = !!preview && sameVersion(textVersion, preview.version);

	// The packet plus every ticked document must fit one email; the server refuses it too.
	const documents = preview?.documents ?? [];
	const ticked = documents.filter((doc) => !unticked.has(doc.id));
	const maxBytes = preview?.maxAttachmentBytes ?? Infinity;
	const packetBytes = (staged?.sizeBytes ?? 0) + ticked.reduce((sum, doc) => sum + (doc.fileSize ?? 0), 0);
	const totalBytes = packetBytes + totalAttachmentBytes(added);
	const overLimit = totalBytes > maxBytes;
	const toggleDocument = (id: string) => {
		setSelectionDirty(true);
		setUnticked((current) => {
			const next = new Set(current);
			if (next.has(id)) next.delete(id); else next.add(id);
			return next;
		});
	};

	const canSubmit = !!preview && !busy && !addedBusy && preview.canSend && !!staged && versionsAgree && textCurrent
		&& !overLimit && !!subject.trim() && !!body.trim();

	/** The PM read the change and stands by their wording. */
	const keepMyWording = () => { if (preview) setTextVersion(preview.version); };
	/** Throw the edited words away for the updated letter. */
	const takeUpdatedWording = () => {
		if (!preview) return;
		setSubject(preview.subject);
		setBody(preview.bodyText);
		setTextVersion(preview.version);
		setDirty(false);
		draft.clear();
	};

	const submit = async () => {
		if (!canSubmit || !preview || !staged) return;
		try {
			const result = await send.mutateAsync({
				id: applicationId,
				body: {
					subject: subject.trim(),
					bodyText: body.trim(),
					attachmentToken: staged.token,
					// The words' version, which `textCurrent` has just proved equals the preview's.
					expectedVersion: textVersion ?? preview.version,
					updateStatus: moveStatus,
					documentIds: ticked.map((doc) => doc.id),
					attachments: added.map((item) => item.ref),
				},
			});
			/**
			 * A transport failure writes nothing, so the letter must survive it: keep the window
			 * and the draft, and let the host explain. Only a recorded letter closes.
			 */
			const { sent, skipped } = result.emailResult;
			onSent(result);
			if (!sent && !skipped) return;
			draft.clear();
			onDismiss();
		} catch (e) {
			// The file expired, or the request / its files moved since staging (every such 409 says
			// "reopen the letter"): bring the text and the PDF back up to date in place.
			if (e instanceof Error && e.message.includes('reopen the letter')) {
				void refetchPreview();
				stageReset();
			}
			// Left open with the text intact. Reported, not swallowed: without this a 409 just
			// stops the spinner and the reviewer cannot tell whether anything went out.
			onError?.(e);
		}
	};

	/**
	 * "Don't Send": the status move alone, as a plain status PUT — the same path every other status
	 * button takes. No letter is recorded and nothing goes to the homeowner. Offered whenever the
	 * move is legal, even when Send is blocked (no submitter email, no template): that is exactly
	 * when someone needs it.
	 */
	const canMoveWithoutSending = !!preview?.statusChange.willChange;
	const moveWithoutSending = async () => {
		if (!canMoveWithoutSending || busy || !preview) return;
		try {
			await moveOnly.mutateAsync({
				id: applicationId,
				data: {
					status: ExteriorApplicationStatus.SubmittedToBoard,
					// The status this window was built from: a move someone made since is a 409, not overwritten.
					expectedStatus: preview.statusChange.from,
				},
			});
			draft.clear();
			onMovedWithoutSending?.();
			onDismiss();
		} catch (e) {
			onMoveError?.(e);
		}
	};

	// Not an async handler with an early await: the web tab must be reserved inside the click.
	const openStaged = () => {
		if (!staged) return;
		dataService.openBoardApprovalAttachment(applicationId, staged.token, staged.fileName).catch((e) => onError?.(e));
	};

	const actions = preview && !isLoading && !error ? (
		<View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
			{/* Left, apart from Cancel/Send, and red: it skips the homeowner's letter entirely. */}
			{canMoveWithoutSending ? (
				<Button
					mode="outlined"
					variant="error"
					onPress={moveWithoutSending}
					loading={moveOnly.isPending}
					disabled={busy}
					testID="btn-board-approval-dont-send"
				>
					Don&apos;t Send
				</Button>
			) : null}
			<View style={{ flex: 1 }} />
			<Button mode="outlined" onPress={onDismiss} disabled={busy}>Cancel</Button>
			<Button
				mode="contained"
				onPress={submit}
				loading={busy}
				disabled={!canSubmit}
				testID="btn-send-board-approval"
			>
				{busy ? 'Sending…' : willSend ? 'Send' : 'Record without emailing'}
			</Button>
		</View>
	) : undefined;

	return (
		<ComposerWindow
			visible={visible}
			onDismiss={onDismiss}
			onSubmit={submit}
			busy={busy}
			startMinimized={startMinimized}
			expandRequest={expandRequest}
			title="Waiting for Board Approval"
			subtitle="Tells the homeowner their request is with the board. Replies come back onto this request."
			footer={actions}
			testID="board-approval-composer"
		>
			{isLoading ? (
				<View style={{ padding: 32, alignItems: 'center' }}><ActivityIndicator /></View>
			) : error || !preview ? (
				<Text style={{ color: theme.colors.error }}>Could not compose the letter.</Text>
			) : (
				<>
					{!willSend ? (
						<View
							testID="board-approval-gate-banner"
							style={{
								flexDirection: 'row', alignItems: 'flex-start',
								backgroundColor: theme.colors.errorContainer,
								borderRadius: 8, padding: 10, marginBottom: 12,
							}}
						>
							<Ionicons name="warning" size={16} color={REVIEW_ACCENT} style={{ marginRight: 8, marginTop: 1 }} />
							<Text style={{ flex: 1, minWidth: 0, fontSize: 12, color: theme.colors.onErrorContainer }}>
								This will be recorded on the request but NOT emailed. {gate?.reason}
							</Text>
						</View>
					) : null}

					{preview.blockers.length ? (
						<View testID="board-approval-blockers" style={{ marginBottom: 12 }}>
							{preview.blockers.map((blocker) => (
								<Text key={blocker} style={{ color: theme.colors.error, fontSize: 12 }}>• {blocker}</Text>
							))}
						</View>
					) : null}

					{preview.notes.length ? (
						<View testID="board-approval-notes" style={{ marginBottom: 12 }}>
							{preview.notes.map((note) => (
								<View key={note} style={{ flexDirection: 'row', alignItems: 'flex-start', marginBottom: 2 }}>
									<Ionicons
										name="information-circle-outline"
										size={14}
										color={theme.colors.onSurfaceVariant}
										style={{ marginRight: 6, marginTop: 1 }}
									/>
									<Text style={{ flex: 1, minWidth: 0, color: theme.colors.onSurfaceVariant, fontSize: 12 }}>
										{note}
									</Text>
								</View>
							))}
						</View>
					) : null}

					{preview.alreadySentAt ? (
						<Text
							style={{ color: theme.colors.onSurfaceVariant, fontSize: 12, marginBottom: 12 }}
							testID="board-approval-already-sent"
						>
							{previousLetterNote(
								'A Waiting for Board Approval letter already went to this homeowner',
								preview.alreadySentAt,
								preview.alreadySentDelivery,
							)}
						</Text>
					) : null}

					<View style={{ marginBottom: 10 }}>
						<Text style={{ fontSize: 12, color: theme.colors.onSurfaceVariant }}>
							To: {preview.to ?? '— no submitter email —'}
						</Text>
						<Text style={{ fontSize: 12, color: theme.colors.onSurfaceVariant }}>
							From: {preview.from.name} &lt;{preview.from.email}&gt;
						</Text>
						<Text style={{ fontSize: 12, color: theme.colors.onSurfaceVariant }}>
							Replies go to: {preview.replyTo}
						</Text>
					</View>

					{/*
						The PM's own words predate a change to the request. Never silently: they
						either stand by them or take the updated letter, and Send waits for that.
					*/}
					{dirty && !textCurrent ? (
						<View
							testID="board-approval-text-stale"
							style={{
								borderRadius: 8, padding: 10, marginBottom: 12,
								backgroundColor: theme.colors.errorContainer,
							}}
						>
							<Text style={{ fontSize: 12, color: theme.colors.onErrorContainer, marginBottom: 6 }}>
								This request changed after you started writing (its status is now{' '}
								{applicationStatusLabel(preview.version.status)}). Check your wording still fits before sending.
							</Text>
							<View style={{ flexDirection: 'row', gap: 8 }}>
								<Button mode="outlined" onPress={keepMyWording} testID="btn-board-approval-keep-wording">
									Keep my wording
								</Button>
								<Button mode="contained" onPress={takeUpdatedWording} testID="btn-board-approval-use-updated">
									Use the updated letter
								</Button>
							</View>
						</View>
					) : null}

					{/*
						The attachment, as a real file. Rendered when this window opened, so what
						"Open" shows is byte-for-byte what Send attaches — not a page it will be
						made from later.
					*/}
					{preview.canSend ? (
						<View
							testID="board-approval-attachment"
							style={{
								flexDirection: 'row', alignItems: 'center', borderWidth: 1, borderRadius: 8,
								borderColor: theme.colors.outlineVariant ?? theme.colors.outline,
								paddingVertical: 8, paddingHorizontal: 10, marginBottom: 12,
							}}
						>
							<Ionicons
								name="document-attach-outline"
								size={18}
								color={stage.isError ? theme.colors.error : theme.colors.primary}
								style={{ marginRight: 8 }}
							/>
							<View style={{ flex: 1, minWidth: 0 }}>
								<Text style={{ fontSize: 13, fontWeight: '600' }} numberOfLines={1}>
									{staged?.fileName ?? preview.attachment.fileName}
								</Text>
								<Text
									testID="board-approval-attachment-state"
									style={{ fontSize: 11, color: stage.isError ? theme.colors.error : theme.colors.onSurfaceVariant }}
								>
									{staged
										? `PDF of the completed application · ${Math.max(1, Math.round(staged.sizeBytes / 1024))} KB`
										: stage.isError
											? `Could not prepare the PDF: ${stage.error instanceof Error ? stage.error.message : 'unknown error'}`
											: 'Preparing the PDF of the completed application…'}
								</Text>
							</View>
							{stage.isPending ? <ActivityIndicator /> : null}
							{staged ? (
								<Pressable
									onPress={openStaged}
									testID="btn-open-board-approval-attachment"
									style={{ paddingVertical: 6, paddingHorizontal: 8 }}
								>
									<Text style={{ color: theme.colors.primary, fontSize: 12, fontWeight: '600' }}>Open</Text>
								</Pressable>
							) : null}
							{stage.isError ? (
								<Pressable
									onPress={() => stageReset()}
									testID="btn-retry-board-approval-attachment"
									style={{ paddingVertical: 6, paddingHorizontal: 8 }}
								>
									<Text style={{ color: theme.colors.primary, fontSize: 12, fontWeight: '600' }}>Try again</Text>
								</Pressable>
							) : null}
						</View>
					) : null}

					{/*
						The request's other files (#608). The packet can only PRINT images, so its
						"Documents (attach separately)" list was all the board ever got. These go out
						as their own attachments, ticked by default — the PM merges them if they want.
					*/}
					{preview.canSend && documents.length ? (
						<View testID="board-approval-documents" style={{ marginBottom: 12 }}>
							<Text style={{ fontSize: 12, fontWeight: '600', marginBottom: 2 }}>
								Also attach the request’s documents ({ticked.length} of {documents.length})
							</Text>
							<Text style={{ fontSize: 11, color: theme.colors.onSurfaceVariant, marginBottom: 4 }}>
								The packet lists these but cannot print them, so they go as separate files.
							</Text>
							{documents.map((doc) => {
								const checked = !unticked.has(doc.id);
								return (
									<View key={doc.id} style={{ flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 3 }}>
										<Pressable
											onPress={() => toggleDocument(doc.id)}
											disabled={busy}
											// `role`/`aria-*`: RN-Web 0.21 drops `accessibilityState`.
											role="checkbox"
											aria-checked={checked}
											testID={`board-approval-document-${doc.id}`}
											style={{ flex: 1, minWidth: 0, flexDirection: 'row', alignItems: 'center', gap: 8 }}
										>
											<Ionicons
												name={checked ? 'checkbox' : 'square-outline'}
												size={16}
												color={checked ? theme.colors.primary : theme.colors.onSurfaceVariant}
											/>
											<Text style={{ flex: 1, minWidth: 0, fontSize: 13 }} numberOfLines={1}>{doc.fileName}</Text>
											{doc.fileSize != null ? (
												<Text style={{ fontSize: 11, color: theme.colors.onSurfaceVariant }}>{formatDocumentSize(doc.fileSize)}</Text>
											) : null}
										</Pressable>
										<Pressable
											// Not awaited: opening must start inside the click for the browser's tab.
											onPress={() => {
												dataService.openAttachmentFile(doc.id, doc.fileName, doc.mimeType ?? 'application/octet-stream')
													.catch((e) => onError?.(e));
											}}
											testID={`btn-open-board-approval-document-${doc.id}`}
											style={{ paddingVertical: 4, paddingHorizontal: 6 }}
										>
											<Text style={{ color: theme.colors.primary, fontSize: 12, fontWeight: '600' }}>Open</Text>
										</Pressable>
									</View>
								);
							})}
							{Number.isFinite(maxBytes) ? (
								<Text
									testID="board-approval-attachments-total"
									style={{ fontSize: 11, marginTop: 4, color: overLimit ? theme.colors.error : theme.colors.onSurfaceVariant }}
								>
									{overLimit
										? `${formatDocumentSize(totalBytes)} with the packet — over the ${formatDocumentSize(maxBytes)} email limit. Untick some files to send.`
										: `${formatDocumentSize(totalBytes)} of ${formatDocumentSize(maxBytes)} with the packet`}
								</Text>
							) : null}
						</View>
					) : null}

					{preview.canSend ? (
						<View style={{ marginBottom: 12 }}>
							<ComposerAttachments
								target={{ kind: 'application', id: applicationId }}
								attachable={preview.attachable ?? { ...NO_ATTACHABLE, maxBytes }}
								items={added}
								onChange={(update) => { setAdded(update); setSelectionDirty(true); }}
								onError={(e) => onError?.(e)}
								onBusyChange={setAddedBusy}
								baseBytes={packetBytes}
								title="Attach more files"
								disabled={busy}
							/>
						</View>
					) : null}

					<Text style={{ fontWeight: '700', marginTop: 6, marginBottom: 4 }}>Subject</Text>
					<TextInput
						value={subject}
						onChangeText={(v) => { setSubject(v); setDirty(true); }}
						testID="board-approval-subject"
					/>

					<Text style={{ fontWeight: '700', marginTop: 12, marginBottom: 4 }}>Message</Text>
					<TextInput
						value={body}
						onChangeText={(v) => { setBody(v); setDirty(true); }}
						multiline
						numberOfLines={12}
						style={{ minHeight: 200 }}
						testID="board-approval-body"
					/>

					<Pressable
						onPress={() => router.push('/settings/email-templates')}
						testID="btn-edit-board-approval-template"
						style={{ alignSelf: 'flex-start', paddingVertical: 8, paddingHorizontal: 4 }}
					>
						<Text style={{ color: theme.colors.primary, fontSize: 12, fontWeight: '600' }}>
							{preview.templateId ? 'Edit the template this came from' : 'Set up a template for this letter'}
						</Text>
					</Pressable>

					{preview.statusChange.allowed && preview.statusChange.from !== preview.statusChange.to ? (
						<Pressable
							onPress={() => setUpdateStatus(!moveStatus)}
							testID="toggle-board-approval-update-status"
							style={{ flexDirection: 'row', alignItems: 'center', marginTop: 12 }}
						>
							<Ionicons
								name={moveStatus ? 'checkbox' : 'square-outline'}
								size={20}
								color={moveStatus ? theme.colors.primary : theme.colors.onSurfaceVariant}
								style={{ marginRight: 8 }}
							/>
							<Text style={{ flex: 1, minWidth: 0, fontSize: 13 }}>
								Also move this request to Waiting for Board Approval
							</Text>
						</Pressable>
					) : null}
				</>
			)}
		</ComposerWindow>
	);
};

export default BoardApprovalModal;
